import { LessonTab } from '@/models/Lesson';
import { StudioStep } from '@/pages/admin/courses/studio/info/CourseStudioInfo.hooks';
import { createSearchParams, URLSearchParamsInit } from 'react-router-dom';

export const withTab = <T extends string>(url: string, tab: T) => {
  const [pathAndQuery, hash = ''] = url.split('#');
  const [path, query = ''] = pathAndQuery.split('?');
  const params = new URLSearchParams(query);
  params.set('tab', tab);
  return `${path}?${params.toString()}${hash ? `#${hash}` : ''}`;
};

type QueryRoute = URLSearchParamsInit;

type RouteParam = string | number;

const DASHBOARD_BASE = '/';
const ADMIN_BASE = '/admin';
const CLASS_BASE = '/class';
const CLASSES_BASE = '/classes';
const STUDENTS_BASE = '/students';
const TEACHERS_BASE = '/teachers';
const TEAM_BASE = '/team';
const HACKATHONS_BASE = '/hackathons';
const UNITS_BASE = '/units';
const COURSES_BASE = '/courses';
export const COURSE_LINES_BASE = '/course-lines';
export const PED_CONTENT_BASE = '/ped-content';
export const MODULE_BASE = '/module';
const LESSON_BASE = '/lesson';
export const EXAM_BASE = '/exam';
const LESSONS_BASE = '/lessons';
export const UNIT_BASE = '/unit';
export const BOOK_BASE = '/book';
export const CHAPTER_BASE = '/chapter';
export const ACTIVITIES_BASE = '/activities';
const PROFILE_BASE = '/profile';
const LOGIN_BASE = '/login';
const LESSONS_FEEDBACK_BASE = '/lessons-feedback';
const PROJECTS_BASE = '/projects';
const PROJECTS_CREATE = 'create/';
const PROJECTS_EDIT = 'edit/';
const HACKATHON_BASE = 'hackathon/';
const HACKATHON_RANKING_BASE = 'ranking/';

const TEACHER_BASE = '/teacher';
const PENDENCIES_BASE = '/pending';
const STUDENT_HISTORY_BASE = '/student-history';
const CALENDAR_BASE = '/calendar';
const NOTIFICATIONS_BASE = '/notifications';
const RECESS_CALENDAR = '/recess-calendar';
const STUDIO_BASE = '/studio';
const BOX_BASE = '/box';
export const HELP_BASE = '/help';
export const CHALLENGE_BASE = '/challenge';
export const SUBCHAPTER_BASE = '/subchapter';
export const EXTRA_COURSES_BASE = '/extra-courses';
const SKILLS_BASE = '/skills';
const ACTIVITY_CORRECTION_BASE = '/activity-correction';

export type CourseRoute = {
  slug: string;
  klassId?: string | number;
};

export type CourseLessonRoute = CourseRoute & {
  lessonId: string | number;
};

export const ROUTES = {
  DASHBOARD: {
    BASE: DASHBOARD_BASE,
  },
  ADMIN: {
    BASE: ADMIN_BASE,
    CLASSES: {
      BASE: `${ADMIN_BASE}${CLASSES_BASE}` as const,
      INFO: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}/info/${createQuery(query)}`,
      LESSONS: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}${ROUTES.LESSONS.BASE}/${createQuery(
          query,
        )}`,
      SHEET: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}/sheet/${createQuery(query)}`,
      MEMBERS: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}/members/${createQuery(query)}`,
      DETAILS: (klassId: RouteParam, query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}/${klassId}/details/${createQuery(query)}`,
      ACTION: (actionId: string, query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}/${actionId}/${createQuery(query)}`,
    },
    STUDENTS: {
      BASE: `${ADMIN_BASE}${STUDENTS_BASE}` as const,
      INFO: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.STUDENTS.BASE}/info/${createQuery(query)}`,
      SHEET: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.STUDENTS.BASE}/sheet/${createQuery(query)}`,
      HISTORIC: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.STUDENTS.BASE}/historic/${createQuery(query)}`,
      DETAILS: (studentId: RouteParam, query?: QueryRoute) =>
        `${ROUTES.ADMIN.STUDENTS.BASE}/${studentId}/details/${createQuery(
          query,
        )}`,
      ACTION: (actionId: string, query?: QueryRoute) =>
        `${ROUTES.ADMIN.STUDENTS.BASE}/${actionId}/${createQuery(query)}`,
    },
    TEACHERS: {
      BASE: `${ADMIN_BASE}${TEACHERS_BASE}` as const,
      INFO: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.TEACHERS.BASE}/info/${createQuery(query)}`,
      LESSONS: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.TEACHERS.BASE}${ROUTES.LESSONS.BASE}/${createQuery(
          query,
        )}`,
      ACTION: (actionId: string, query?: QueryRoute) =>
        `${ROUTES.ADMIN.TEACHERS.BASE}/${actionId}/${createQuery(query)}`,
    },
    TEAM: {
      BASE: `${ADMIN_BASE}${TEAM_BASE}` as const,
      INFO: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.TEAM.BASE}/info/${createQuery(query)}`,
      ACTION: (actionId: string, query?: QueryRoute) =>
        `${ROUTES.ADMIN.TEAM.BASE}/${actionId}/${createQuery(query)}`,
    },
    HACKATHONS: {
      BASE: `${ADMIN_BASE}${HACKATHONS_BASE}` as const,
    },
    UNITS: {
      BASE: `${ADMIN_BASE}${UNITS_BASE}` as const,
      INFO: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.UNITS.BASE}/info/${createQuery(query)}`,
      RECESS_CALENDAR: {
        BASE: RECESS_CALENDAR,
        INFO: (unitId: number | string, query?: QueryRoute) =>
          `${ROUTES.ADMIN.UNITS.BASE}/${unitId}${RECESS_CALENDAR}/${createQuery(
            query,
          )}`,
      },

      ACTION: (actionId: string, query?: QueryRoute) =>
        `${ROUTES.ADMIN.UNITS.BASE}/${actionId}/${createQuery(query)}`,
    },

    COURSES: {
      BASE: `${ADMIN_BASE}${COURSES_BASE}` as const,
      INFO: (query?: QueryRoute) =>
        `${ROUTES.ADMIN.CLASSES.BASE}/info/${createQuery(query)}`,
      ACTION: (actionId: string, query?: QueryRoute) =>
        `${ROUTES.ADMIN.COURSES.BASE}/${actionId}/${createQuery(query)}`,
      VERSIONING: (slug: string) =>
        `${ROUTES.ADMIN.COURSES.BASE}/${slug}${ROUTES.LESSONS.BASE}/versioning`,
      EDITING: (slug: string) =>
        `${ROUTES.ADMIN.COURSES.BASE}/${slug}${ROUTES.LESSONS.BASE}/editing`,
      DRAFT: (slug: string) =>
        `${ROUTES.ADMIN.COURSES.BASE}/${slug}${ROUTES.LESSONS.BASE}/draft`,
    },
    COURSE_LINES: {
      BASE: `${ADMIN_BASE}${COURSE_LINES_BASE}` as const,
      INFO: (courseLineId: RouteParam) =>
        `${ROUTES.ADMIN.COURSE_LINES.BASE}/${courseLineId}`,
      MODULE: (courseLineId: RouteParam, moduleId: RouteParam) =>
        `${ROUTES.ADMIN.COURSE_LINES.INFO(
          courseLineId,
        )}${MODULE_BASE}/${moduleId}`,
      PED_CONTENT: (
        courseLineId: RouteParam,
        moduleId: RouteParam,
        slug: RouteParam,
      ) =>
        `${ROUTES.ADMIN.COURSE_LINES.MODULE(
          courseLineId,
          moduleId,
        )}${PED_CONTENT_BASE}/${slug}`,
      UNIT: (
        courseLineId: RouteParam,
        moduleId: RouteParam,
        slug: RouteParam,
        unitId: RouteParam,
      ) =>
        `${ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(
          courseLineId,
          moduleId,
          slug,
        )}${UNIT_BASE}/${unitId}`,
      LESSON: (
        courseLineId: RouteParam,
        moduleId: RouteParam,
        slug: RouteParam,
        unitId: RouteParam,
        lessonId: RouteParam,
      ) =>
        `${ROUTES.ADMIN.COURSE_LINES.UNIT(
          courseLineId,
          moduleId,
          slug,
          unitId,
        )}${LESSON_BASE}/${lessonId}`,
      EXAM: (
        courseLineId: RouteParam,
        moduleId: RouteParam,
        slug: RouteParam,
        examId: RouteParam,
      ) =>
        `${ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(
          courseLineId,
          moduleId,
          slug,
        )}${EXAM_BASE}/${examId}`,
    },
    CALENDARS: {
      BASE: '/calendars' as const,
    },
  },

  CLASS: {
    BASE: CLASS_BASE,
    PROGRESS: (klassId: string | number) =>
      `${ROUTES.CLASS.BASE}/${klassId}/progress`,
    LESSON_REPORT: (
      klassId: string | number,
      scheduledLessonId: string | number,
    ) => `${ROUTES.CLASS.BASE}/${klassId}/lesson-report/${scheduledLessonId}`,
  },
  COURSES: {
    BASE: '/courses' as const,
    INFO: (slug: string, klassId?: string | number) =>
      `${ROUTES.COURSES.BASE}/${slug}${
        klassId ? `${ROUTES.CLASS.BASE}/${klassId}` : ''
      }`,
    LESSON: {
      INFO: (
        slug: string,
        lessonId: string | number,
        klassId?: string | number,
      ) =>
        `${ROUTES.COURSES.BASE}/${slug}${ROUTES.LESSON.BASE}/${lessonId}${
          klassId ? `${ROUTES.CLASS.BASE}/${klassId}` : ''
        }`,
      BOOK: {
        CHAPTER: ({
          slug,
          lessonId,
          klassId,
          chapterOrder,
        }: CourseLessonRoute & { chapterOrder?: string | number }) =>
          `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}${
            ROUTES.BOOK.BASE
          }${ROUTES.CHAPTER.BASE}/${chapterOrder}`,
        SUBCHAPTER: ({
          slug,
          lessonId,
          klassId,
          chapterOrder,
          subchapterOrder,
        }: CourseLessonRoute & {
          chapterOrder?: string | number;
          subchapterOrder?: string | number;
        }) =>
          `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}${
            ROUTES.BOOK.BASE
          }${
            ROUTES.CHAPTER.BASE
          }/${chapterOrder}/subchapter/${subchapterOrder}`,
      },
      TEACHER_BOOK: {
        CHAPTER: ({
          slug,
          lessonId,
          klassId,
          chapterOrder,
        }: CourseLessonRoute & { chapterOrder?: string | number }) =>
          `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}/t${
            ROUTES.BOOK.BASE
          }${ROUTES.CHAPTER.BASE}/${chapterOrder}`,
      },
      MATERIAL: {
        CHAPTER: ({
          slug,
          lessonId,
          klassId,
          chapterOrder,
        }: CourseLessonRoute & { chapterOrder: string | number }) =>
          `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}/material${
            ROUTES.CHAPTER.BASE
          }/${chapterOrder}`,
        SUBCHAPTER: ({
          slug,
          lessonId,
          klassId,
          chapterOrder,
          subchapterOrder,
        }: CourseLessonRoute & {
          chapterOrder: string | number;
          subchapterOrder: string | number;
        }) =>
          `${ROUTES.COURSES.LESSON.MATERIAL.CHAPTER({
            slug,
            lessonId,
            klassId,
            chapterOrder,
          })}${SUBCHAPTER_BASE}/${subchapterOrder}`,
      },

      ACTIVITIES: ({ slug, lessonId, klassId }: CourseLessonRoute) =>
        `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}${
          ROUTES.ACTIVITIES.BASE
        }`,
      ACTIVITY: ({
        slug,
        lessonId,
        klassId,
        activityProgressId,
      }: CourseLessonRoute & { activityProgressId: string | number }) =>
        `${ROUTES.COURSES.LESSON.ACTIVITIES({
          slug,
          lessonId,
          klassId,
        })}/${activityProgressId}`,
      ACTIVITY_SUCCESS: (
        params: CourseLessonRoute & { activityProgressId: string | number },
      ) => `${ROUTES.COURSES.LESSON.ACTIVITY(params)}/success`,
      CHALLENGE: ({ slug, lessonId, klassId }: CourseLessonRoute) =>
        `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}/challenge`,
      HOMEWORK: ({ slug, lessonId, klassId }: CourseLessonRoute) =>
        `${ROUTES.COURSES.LESSON.INFO(
          slug,
          lessonId,
          klassId,
        )}${ACTIVITIES_BASE}/homework`,
      HELP: ({ slug, lessonId, klassId }: CourseLessonRoute) =>
        `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}/help`,
      RESOURCES: ({ slug, lessonId, klassId }: CourseLessonRoute) =>
        `${ROUTES.COURSES.LESSON.INFO(slug, lessonId, klassId)}/resources`,
    },
    BOX: {
      BASE: `${COURSES_BASE}${BOX_BASE}` as const,
      INFO: (slug: string) => `${ROUTES.COURSES.BOX.BASE}/${slug}`,
      LESSON: (slug: string, lessonOrder: number) =>
        `${ROUTES.COURSES.BOX.INFO(slug)}/lesson/${lessonOrder}`,
      TAB: (slug: string, lessonOrder: number, tab: LessonTab) =>
        `${ROUTES.COURSES.BOX.LESSON(slug, lessonOrder)}?tab=${tab}`,
    },
    STUDIO: {
      BASE: `${COURSES_BASE}${STUDIO_BASE}` as const,
      INFO: (slug: string) => `${ROUTES.COURSES.STUDIO.BASE}/${slug}`,
      STEP: (slug: string, step: StudioStep) =>
        `${ROUTES.COURSES.STUDIO.BASE}/${slug}/${step}`,
    },
  },
  LESSONS: {
    BASE: LESSONS_BASE,
    DETACHED: {
      BASE: `${ADMIN_BASE}${LESSONS_BASE}/:lessonId` as const,
      EDIT: (lessonId: number) => `${ADMIN_BASE}${LESSONS_BASE}/${lessonId}`,
      MATERIAL: (lessonId: number, bookType: string, bookId: number) =>
        `${ADMIN_BASE}${LESSONS_BASE}/${lessonId}/${bookType}/${bookId}`,
      NESTED_MATERIAL: ':bookType/:bookId',
      CHAPTER: (
        lessonId: number,
        bookType: string,
        bookId: number,
        chapterId: number,
      ) =>
        `${ADMIN_BASE}${LESSONS_BASE}/${lessonId}/${bookType}/${bookId}/chapter/${chapterId}`,
      NESTED_CHAPTER: ':bookType/:bookId/chapter/:chapterId',
      NESTED_ACTIVITY: 'activities/:activityId',
      ACTIVITY: (lessonId: string | number, activityId: string | number) =>
        `${ADMIN_BASE}${LESSONS_BASE}/${lessonId}/activities/${activityId}`,
    },
  },
  LESSON: {
    BASE: LESSON_BASE,
  },
  NOTIFICATIONS: {
    BASE: NOTIFICATIONS_BASE,
  },
  BOOK: {
    BASE: BOOK_BASE,
  },
  CHAPTER: {
    BASE: CHAPTER_BASE,
  },
  ACTIVITIES: {
    BASE: ACTIVITIES_BASE,
    ACTIVITY_CORRECTION: {
      BASE: ACTIVITY_CORRECTION_BASE,
      ACTIVITY: (klassId: RouteParam, activityProgressId: RouteParam) =>
        `${ROUTES.CLASS.BASE}/${klassId}/activity/${activityProgressId}`,
      PROGRESS: (klassId: RouteParam, activityProgressId: RouteParam) =>
        `${ROUTES.ACTIVITIES.ACTIVITY_CORRECTION.ACTIVITY(
          klassId,
          activityProgressId,
        )}/correction`,
      CORRECTED: (klassId: RouteParam, activityProgressId: RouteParam) =>
        `${ROUTES.ACTIVITIES.ACTIVITY_CORRECTION.ACTIVITY(
          klassId,
          activityProgressId,
        )}/corrected`,
      AI: (klassId: RouteParam, activityProgressId: RouteParam) =>
        `${ROUTES.ACTIVITIES.ACTIVITY_CORRECTION.ACTIVITY(
          klassId,
          activityProgressId,
        )}/ai-correction`,
    },
  },
  PROFILE: {
    BASE: PROFILE_BASE,
    INFO: (username: string) => `${ROUTES.PROFILE.BASE}/${username}`,
  },
  TEACHER: {
    BASE: TEACHER_BASE,
  },
  PENDENCIES: {
    BASE: PENDENCIES_BASE,
  },
  ACCOUNT: {
    BASE: '/account' as const,
  },
  EXAM_SCHEDULING: {
    BASE: `${CLASS_BASE}${EXAM_BASE}/scheduling` as const,
    LIST: (klassId?: number | string) => {
      const list = `${CLASS_BASE}${EXAM_BASE}/scheduling`;
      if (!klassId) return list;
      return `${list}?klassId=${klassId}`;
    },
    SCHEDULE: (
      klassId: number | string,
      examId: number | string,
      scheduledLessonId?: number | string,
    ) => {
      const schedule = `${CLASS_BASE}/${klassId}${EXAM_BASE}/${examId}/scheduling`;
      if (!scheduledLessonId) return schedule;
      return `${schedule}?scheduledLessonId=${scheduledLessonId}`;
    },
  },
  EXAM_CORRECTION: {
    PROGRESS: (klassId: RouteParam, studentExamId: RouteParam) =>
      `${CLASS_BASE}/${klassId}${EXAM_BASE}/${studentExamId}/correction`,
    CORRECTED: (klassId: RouteParam, studentExamId: RouteParam) =>
      `${CLASS_BASE}/${klassId}${EXAM_BASE}/${studentExamId}/corrected`,
  },
  PROJECTS: {
    BASE: PROJECTS_BASE,
    CREATE: `${PROJECTS_BASE}/${PROJECTS_CREATE}` as const,

    EDIT: {
      BASE: `${PROJECTS_BASE}/${PROJECTS_EDIT}:id` as const,
      INFO: (projectId: number | string) =>
        `${PROJECTS_BASE}/${PROJECTS_EDIT}${projectId}`,
    },
    INFO: (projectId: number | string) =>
      `${ROUTES.PROJECTS.BASE}/${projectId}`,
  },
  HACKATHON: {
    BASE: HACKATHON_BASE,
    RANKING: {
      BASE: `${HACKATHON_BASE}${HACKATHON_RANKING_BASE}` as const,
    },
  },
  STUDENT_HISTORY: {
    BASE: STUDENT_HISTORY_BASE,
    INFO: (studentId: number | string) =>
      `${ROUTES.STUDENT_HISTORY.BASE}/${studentId}`,
  },
  FORBIDDEN: {
    BASE: '/forbidden' as const,
  },
  LOGIN: {
    BASE: LOGIN_BASE,
    RESET_PASSWORD: (uid: string, token: string) =>
      `${ROUTES.LOGIN.BASE}/reset-password/${uid}/u/${token}`,
    PASSWORD_RESET: (urlToken: string) =>
      `${ROUTES.LOGIN.BASE}/password-reset/new-password/${urlToken}`,
    PASSWORD_CHANGED: `${LOGIN_BASE}/password-changed`,
  },
  LESSONS_FEEDBACK: {
    BASE: LESSONS_FEEDBACK_BASE,
  },
  CALENDAR: {
    BASE: CALENDAR_BASE,
  },
  EXTRA_COURSES: {
    BASE: EXTRA_COURSES_BASE,
  },
  SKILLS: {
    BASE: SKILLS_BASE,
  },
};

const createQuery = (query?: QueryRoute) =>
  query ? `?${createSearchParams(query)}` : '';
